import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { AdminBreadcrumb, AdminBreadcrumbSegment } from "@/components/layout/AdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  FileText, 
  ClipboardCheck, 
  Building2,
  Activity,
  Users,
  MessageSquare,
  LayoutGrid,
  Loader2,
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  Eye
} from "lucide-react";

import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { useAuth } from "@/context/Auth";
import { supabase } from "@/integrations/supabase/client";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import { getLogoUrl } from "@/utils/getLogoUrl";
import { fetchAdminResponses } from "@/api/adminResponses";
import { resolveOrgLogo } from "@/utils/resolveOrgLogo";
import { format } from "date-fns";
import { ReviewerNotesDrawer } from "@/components/assessment/ReviewerNotesDrawer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AssessmentUsersTab } from "@/components/assessment/AssessmentUsersTab";
import { isSelfAssessmentOnly } from "@/lib/questionnaireType";
import { QaToolkitPanel } from "@/components/qa/QaToolkitPanel";

interface AdminContext {
  clientId: string;
  clientName: string;
  subOrgId: string;
  subOrgName: string;
  portfolioId: string;
  portfolioName: string;
  assessmentId?: string;
  assessmentName?: string;
}

interface AssessmentHeaderData {
  id: string;
  status: string;
  created_at: string;
  submitted_at: string | null;
  questionnaire_id: string;
  portfolios: {
    id: string;
    name: string;
    category: string | null;
  } | null;
  questionnaires: {
    id: string;
    meta: Record<string, unknown> | null;
  } | null;
}

interface QuestionInfo {
  question_id: number;
  domain: string;
  question_key: string;
}

interface ResponseData {
  question_id: number;
  score: number | null;
  comment: string | null;
  evidence_links: string[] | null;
}

// Standardized thresholds for Phase A
const SCORE_THRESHOLDS = {
  STRONG: 80,
  ADEQUATE: 50,
};

// Resilience Rating mapping (presentation-layer only)
const getResilienceRating = (score: number | null): { label: string; colorClass: string; bgClass: string } => {
  if (score === null) return { label: "Not Rated", colorClass: "text-muted-foreground", bgClass: "bg-muted" };
  if (score >= 86) return { label: "Extensive", colorClass: "text-green-600 dark:text-green-400", bgClass: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700" };
  if (score >= 71) return { label: "Strong", colorClass: "text-blue-600 dark:text-blue-400", bgClass: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700" };
  if (score >= 41) return { label: "Adequate", colorClass: "text-yellow-600 dark:text-yellow-400", bgClass: "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700" };
  return { label: "Minimal", colorClass: "text-orange-600 dark:text-orange-400", bgClass: "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700" };
};

const getStatusFromScore = (score: number | null): { label: string; color: string; bgClass: string } => {
  if (score === null) return { label: "Not started", color: "text-muted-foreground", bgClass: "bg-muted" };
  if (score >= SCORE_THRESHOLDS.STRONG) return { label: "Strong", color: "text-green-600", bgClass: "bg-green-500" };
  if (score >= SCORE_THRESHOLDS.ADEQUATE) return { label: "Adequate", color: "text-amber-600", bgClass: "bg-amber-500" };
  return { label: "Needs Attention", color: "text-destructive", bgClass: "bg-destructive" };
};

const getStatusBadgeVariant = (score: number | null): "default" | "secondary" | "destructive" | "outline" => {
  if (score === null) return "outline";
  if (score >= SCORE_THRESHOLDS.STRONG) return "default";
  if (score >= SCORE_THRESHOLDS.ADEQUATE) return "secondary";
  return "destructive";
};

export default function AssessmentOverview() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { role: effectiveRole } = useEffectiveRole();
  const { tier } = useAuth();
  const isTier1 = tier === "tier_1";
  
  // Get admin context from navigation state
  const adminContext = (location.state as { adminContext?: AdminContext } | null)?.adminContext;
  
  const [headerData, setHeaderData] = useState<AssessmentHeaderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [participantCounts, setParticipantCounts] = useState({ respondents: 0, reviewers: 0 });

  // Maturity summary from RPC (replaces useSelfAssessmentScores)
  interface MaturitySummary {
    assessment_status: string;
    current_review_cycle: number;
    respondent_overall_score: number | null;
    reviewer_overall_score: number | null;
    respondent_by_domain: Record<string, number>;
    reviewer_by_domain: Record<string, number>;
    respondent_completion_pct: number;
    reviewer_completion_pct: number;
  }
  const [maturitySummary, setMaturitySummary] = useState<MaturitySummary | null>(null);
  
  // Domain overview data: per CSV Pro domain aggregations
  interface DomainStats {
    total: number;
    respondentAnswered: number;
    reviewerReviewed: number;
    notesCount: number;
    score: number | null; // Domain maturity score (0-100)
  }
  const [domainData, setDomainData] = useState<Record<string, DomainStats>>({});
  
  // Store raw question and response data for drawer
  const [questionInfoList, setQuestionInfoList] = useState<QuestionInfo[]>([]);
  const [responseDataList, setResponseDataList] = useState<ResponseData[]>([]);
  const [reviewerRatingsList, setReviewerRatingsList] = useState<{ question_id: number; reviewer_score: number | null; reviewer_notes: string | null }[]>([]);
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  
  // Collapsible notes panel state
  const [notesExpanded, setNotesExpanded] = useState(false);
  
  // Logo state for hierarchy fallback
  const [portfolioLogo, setPortfolioLogo] = useState<string | null>(null);
  const [clientLogo, setClientLogo] = useState<string | null>(null);
  
  // NIST domain code to function name mapping (for RPC domain data)
  const NIST_CODE_TO_FUNCTION: Record<string, string> = {
    GV: "Govern", ID: "Identify", PR: "Protect", DE: "Detect", RS: "Respond", RC: "Recover"
  };

  // CSV Pro domains in display order
  const CSV_PRO_DOMAINS = [
    "Governance",
    "Risk Management",
    "Supply Chain Management",
    "Physical Security & Environmental Controls",
    "Asset Management",
    "Zero Trust Architecture",
    "Security Operations",
  ] as const;

  // Map NIST CSF framework domains to CSV Pro domains
  const FRAMEWORK_TO_CSV_DOMAIN: Record<string, string[]> = {
    "Govern": ["Governance", "Risk Management"],
    "Identify": ["Asset Management", "Supply Chain Management"],
    "Protect": ["Zero Trust Architecture", "Physical Security & Environmental Controls"],
    "Detect": ["Security Operations"],
    "Respond": ["Security Operations"],
    "Recover": ["Risk Management", "Governance"],
  };

  useEffect(() => {
    async function fetchAssessmentData() {
      if (!assessmentId) return;
      
      setLoading(true);

      // Start maturity summary RPC immediately (handles its own auth)
      const maturityPromise = (supabase.rpc as any)('portfolio_get_maturity_summary', {
        p_assessment_id: assessmentId,
      });
      
      // Branch by role:
      // - admin → admin_get_assessment_overview (SECURITY DEFINER, row_security off)
      // - PM / client_admin → pm_get_assessment_overview (SECURITY DEFINER, scoped)
      // - respondent / reviewer → direct RLS-protected select on assessments
      const isAdminRole = effectiveRole === "admin";
      const isPMRole = effectiveRole === "portfolio_manager" || effectiveRole === "client_admin";
      const isRespondentOrReviewerRole = !isAdminRole && !isPMRole;

      console.log('[AssessmentOverview] role branch =', effectiveRole);

      let overviewResult: { data: any; error: any };
      let assigneesResult: { data: any; error: any };

      if (isAdminRole) {
        [overviewResult, assigneesResult] = await Promise.all([
          supabase.rpc("admin_get_assessment_overview", { p_assessment_id: assessmentId }),
          (supabase.rpc as any)('admin_list_assessment_assignees', { p_assessment_id: assessmentId }),
        ]);
      } else if (isPMRole) {
        [overviewResult, assigneesResult] = await Promise.all([
          (supabase.rpc as any)("pm_get_assessment_overview", { p_assessment_id: assessmentId }),
          (supabase.rpc as any)('pm_list_assessment_assignees', { p_assessment_id: assessmentId }),
        ]);
      } else {
        // Respondent / Reviewer: use SECURITY DEFINER RPCs
        const { data: rpcData, error: rpcErr } = await (supabase.rpc as any)(
          'respondent_get_assessment',
          { p_assessment_id: assessmentId }
        );

        if (rpcErr || !rpcData || (Array.isArray(rpcData) && rpcData.length === 0)) {
          overviewResult = { data: null, error: rpcErr ?? { message: "Assessment not found" } };
        } else {
          const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
          overviewResult = {
            data: [{
              id: row.id,
              status: row.status,
              created_at: row.created_at,
              submitted_at: row.submitted_at,
              questionnaire_id: row.questionnaire_id,
              portfolio_id: row.org_id,
              portfolio_name: row.org_name ?? "Your Organization",
              portfolio_category: null,
              org_id: row.org_id,
              questionnaire_meta: {},
            }],
            error: null,
          };
        }

        // Respondents/reviewers: use RPC for assignees
        const { data: aaRows, error: aaErr } = await (supabase.rpc as any)(
          'respondent_get_assessment_assignees',
          { p_assessment_id: assessmentId }
        );
        assigneesResult = { data: aaRows ?? [], error: aaErr };
      }

      // Build header data from RPC result
      let headerResult: { data: AssessmentHeaderData | null; error: any } = { data: null, error: null };
      
      if (overviewResult.error) {
        headerResult.error = overviewResult.error;
      } else if (!overviewResult.data || (Array.isArray(overviewResult.data) && overviewResult.data.length === 0)) {
        headerResult.error = { message: "Assessment not found" };
      } else {
        // RPC returns a single row (as array with one element)
        const overview = Array.isArray(overviewResult.data) ? overviewResult.data[0] : overviewResult.data;
        
        // Capture portfolio logo if returned by the RPC
        if (overview.portfolio_logo) {
          setPortfolioLogo(overview.portfolio_logo);
        }

        headerResult.data = {
          id: overview.id,
          status: overview.status,
          created_at: overview.created_at,
          submitted_at: overview.submitted_at,
          questionnaire_id: overview.questionnaire_id,
          portfolios: {
            id: overview.portfolio_id,
            name: overview.portfolio_name,
            category: overview.portfolio_category
          },
          questionnaires: {
            id: overview.questionnaire_id,
            meta: (overview.questionnaire_meta ?? {}) as Record<string, unknown>
          }
        };
      }

      if (headerResult.error) {
        console.error("Error fetching assessment:", headerResult.error);
        setError(headerResult.error.message);
      } else if (!headerResult.data) {
        setError("Assessment not found");
      } else {
        setHeaderData(headerResult.data as unknown as AssessmentHeaderData);
        
        // Fetch progress data using the questionnaire_id
        const questionnaireId = headerResult.data.questionnaire_id;
        
        // Use admin RPC for questionnaire items if admin
        let questionsResult;
        if (isAdminRole) {
          console.log('[ADMIN] RPC=admin_list_questionnaire_items');
          const { data, error } = await (supabase.rpc as any)('admin_list_questionnaire_items', {
            p_questionnaire_id: questionnaireId,
          });
          const questionIds = (data ?? []).map((q: any) => q.question_id);
          const { data: questionsData } = await (supabase.rpc as any)('admin_list_questions_by_ids', {
            p_ids: questionIds,
          });
          questionsResult = {
            data: (data ?? []).map((qi: any) => ({
              question_id: qi.question_id,
              questions: questionsData?.find((q: any) => q.id === qi.question_id) ?? { id: qi.question_id, domain: '', question_key: '' }
            })),
            error
          };
        } else if (isPMRole) {
          // PM branch: use security-definer RPC
          const overview = Array.isArray(overviewResult.data) ? overviewResult.data[0] : overviewResult.data;
          const portfolioId = overview?.org_id as string | undefined;
          console.log("PM RPC payload:", { portfolioId, questionnaireId });
          if (!portfolioId) {
            console.error("portfolioId is null — cannot call PM RPC");
            return;
          }
          const { data: pmItems, error: pmItemsErr } = await (supabase.rpc as any)('pm_get_questionnaire_items', {
            p_portfolio_id: portfolioId,
            p_questionnaire_id: questionnaireId,
          });
          questionsResult = {
            data: (pmItems ?? []).map((qi: any) => ({
              question_id: qi.question_id,
              questions: { id: qi.question_id, domain: qi.domain, question_key: qi.question_key }
            })),
            error: pmItemsErr
          };
        } else {
          // Respondent / Reviewer: use SECURITY DEFINER RPC for questionnaire items
          const { data: rpcItems, error: rpcItemsErr } = await (supabase.rpc as any)(
            'respondent_get_questionnaire_items',
            { p_assessment_id: assessmentId, p_questionnaire_id: questionnaireId }
          );
          questionsResult = {
            data: (rpcItems ?? []).map((qi: any) => ({
              question_id: qi.question_id,
              questions: { id: qi.question_id, domain: qi.domain, question_key: qi.question_key }
            })),
            error: rpcItemsErr
          };
        }

        // Fetch responses by role
        let adminResponses: { question_id: number; score: number | null; comment: string | null; evidence_links: string[] | null; updated_at: string }[];
        if (isAdminRole) {
          adminResponses = await fetchAdminResponses(assessmentId, null);
        } else if (isPMRole) {
          const portfolioIdForResp = (Array.isArray(overviewResult.data) ? overviewResult.data[0] : overviewResult.data)?.org_id as string;
          const { data: pmResp, error: pmRespErr } = await (supabase.rpc as any)('pm_get_responses', {
            p_portfolio_id: portfolioIdForResp,
            p_assessment_id: assessmentId,
          });
          if (pmRespErr) {
            console.error("[PM] pm_get_responses failed:", pmRespErr);
            adminResponses = [];
          } else {
            adminResponses = (pmResp ?? []).map((r: any) => ({
              question_id: r.question_id,
              score: r.score,
              comment: r.comment,
              evidence_links: Array.isArray(r.evidence_links) ? r.evidence_links : [],
              updated_at: r.updated_at,
            }));
          }
        } else {
          // Respondent / Reviewer: use SECURITY DEFINER RPC
          const { data: respRpc, error: respErr } = await (supabase.rpc as any)(
            'respondent_get_responses',
            { p_assessment_id: assessmentId }
          );
          if (respErr) {
            console.error("[Respondent] respondent_get_responses failed:", respErr);
            adminResponses = [];
          } else {
            adminResponses = (respRpc ?? []).map((r: any) => ({
              question_id: r.question_id,
              score: r.score,
              comment: r.comment,
              evidence_links: Array.isArray(r.evidence_links) ? r.evidence_links : [],
              updated_at: r.updated_at,
            }));
          }
        }
        console.log("[AssessmentOverview] responses", { assessmentId, count: adminResponses.length });

        const questions = questionsResult.data ?? [];
        const responses = adminResponses.map(r => ({
          question_id: r.question_id,
          score: r.score,
          comment: r.comment,
          evidence_links: r.evidence_links
        }));
        const totalQuestions = questions.length;
        
        // Build question info list and domain map
        const questionDomainMap: Record<number, string> = {};
        const questionInfoArr: QuestionInfo[] = [];
        for (const q of questions) {
          const qData = q.questions as unknown as { id: number; domain: string; question_key: string } | null;
          if (qData) {
            questionDomainMap[q.question_id] = qData.domain ?? "";
            questionInfoArr.push({
              question_id: q.question_id,
              domain: qData.domain ?? "",
              question_key: qData.question_key ?? ""
            });
          }
        }
        setQuestionInfoList(questionInfoArr);
        setResponseDataList(responses);
        
        // Build response lookup by question_id
        const responseMap: Record<number, { score: number | null; comment: string | null; evidence_links: string[] | null }> = {};
        for (const r of responses) {
          responseMap[r.question_id] = r;
        }
        
        // Respondent: answered if score is not null (respondent selects a maturity score)
        const respondentAnswered = new Set(
          responses
            .filter(r => r.score !== null && r.score !== undefined)
            .map(r => r.question_id)
        ).size;
        
        // Fetch reviewer_ratings separately for reviewer progress and notes
        let reviewerRatings: { question_id: number; reviewer_score: number | null; reviewer_notes: string | null }[] = [];
        if (isAdminRole) {
          const { data: ratingsData } = await (supabase.rpc as any)('admin_list_reviewer_ratings', {
            p_assessment_id: assessmentId,
          });
          reviewerRatings = (ratingsData ?? []).map((r: any) => ({
            question_id: r.question_id,
            reviewer_score: r.reviewer_score,
            reviewer_notes: r.reviewer_notes,
          }));
        } else if (isPMRole) {
          const { data: ratingsData } = await (supabase.rpc as any)('pm_get_reviewer_ratings', {
            p_assessment_id: assessmentId,
          });
          reviewerRatings = (ratingsData ?? []).map((r: any) => ({
            question_id: r.question_id,
            reviewer_score: r.reviewer_score,
            reviewer_notes: r.reviewer_notes,
          }));
        } else {
          // Respondent/Reviewer: use respondent_get_reviewer_ratings or reviewer_get_ratings
          const { data: ratingsData } = await (supabase.rpc as any)('respondent_get_reviewer_ratings', {
            p_assessment_id: assessmentId,
          });
          reviewerRatings = (ratingsData ?? []).map((r: any) => ({
            question_id: r.question_id,
            reviewer_score: r.reviewer_score,
            reviewer_notes: r.reviewer_notes,
          }));
        }
        
        // Build reviewer ratings lookup by question_id
        const reviewerRatingsMap: Record<number, { reviewer_score: number | null; reviewer_notes: string | null }> = {};
        for (const r of reviewerRatings) {
          reviewerRatingsMap[r.question_id] = r;
        }
        
        // Aggregate per CSV Pro domain (notes counts only — scores come from maturity RPC)
        const domainStats: Record<string, DomainStats> = {};
        for (const csvDomain of CSV_PRO_DOMAINS) {
          domainStats[csvDomain] = { total: 0, respondentAnswered: 0, reviewerReviewed: 0, notesCount: 0, score: null };
        }
        
        for (const q of questions) {
          const qData = q.questions as unknown as { id: number; domain: string } | null;
          const frameworkDomain = qData?.domain ?? "";
          const csvDomains = FRAMEWORK_TO_CSV_DOMAIN[frameworkDomain] ?? [];
          const reviewerRating = reviewerRatingsMap[q.question_id];
          
          for (const csvDomain of csvDomains) {
            if (domainStats[csvDomain]) {
              domainStats[csvDomain].total += 1;
              
              // Notes count (reviewer_notes from reviewer_ratings)
              if (reviewerRating && typeof reviewerRating.reviewer_notes === "string" && reviewerRating.reviewer_notes.trim() !== "") {
                domainStats[csvDomain].notesCount += 1;
              }
            }
          }
        }
        
        setDomainData(domainStats);
        setReviewerRatingsList(reviewerRatings);

        // Await maturity summary RPC
        const { data: matData, error: matErr } = await maturityPromise;
        if (matErr) {
          console.error("[AssessmentOverview] portfolio_get_maturity_summary failed:", matErr);
        } else if (matData) {
          setMaturitySummary(matData as MaturitySummary);
        }
      }

      if (assigneesResult.data) {
        const respondents = assigneesResult.data.filter(a => a.role === "respondent").length;
        const reviewers = assigneesResult.data.filter(a => a.role === "reviewer").length;
        setParticipantCounts({ respondents, reviewers });
      }

      setLoading(false);
    }

    if (effectiveRole === null) return; // wait for role to resolve
    fetchAssessmentData();
  }, [assessmentId, effectiveRole, refreshKey]);

  const isPortfolioManager = effectiveRole === "portfolio_manager";
  const isClientAdmin = effectiveRole === "client_admin";
  const isAdmin = effectiveRole === "admin";
  const isReviewer = effectiveRole === "reviewer" || effectiveRole === "master_reviewer";
  const isRespondent = effectiveRole === "respondent";
  
  // Read-only roles: cannot edit questionnaire
  const isReadOnlyRole = isPortfolioManager || isClientAdmin || isAdmin;
  
  // Roles that can view reviewer notes drill-in
  const canViewNotesDrillIn = isPortfolioManager || isClientAdmin || isAdmin || isReviewer;

  // Fetch client logo for PM context header
  useEffect(() => {
    if (!isPortfolioManager || !headerData?.portfolios?.id) return;
    (async () => {
      try {
        const { data } = await (supabase as any).rpc("pm_portfolio_context", { _portfolio_id: headerData.portfolios!.id });
        if (data?.[0]?.client_logo) {
          setClientLogo(data[0].client_logo);
        }
      } catch (e) {
        console.warn("[AssessmentOverview] PM logo lookup failed:", e);
      }
    })();
  }, [isPortfolioManager, headerData?.portfolios?.id]);

  // Build admin breadcrumb segments with proper navigation state
  const adminBreadcrumbSegments: AdminBreadcrumbSegment[] | undefined = isAdmin && adminContext
    ? [
        { 
          label: adminContext.clientName, 
          href: "/admin",
          state: { navigateTo: "subOrgs", clientId: adminContext.clientId, clientName: adminContext.clientName }
        },
        { 
          label: adminContext.subOrgName, 
          href: "/admin",
          state: { navigateTo: "portfolios", clientId: adminContext.clientId, clientName: adminContext.clientName, subOrgId: adminContext.subOrgId, subOrgName: adminContext.subOrgName }
        },
        { 
          label: adminContext.portfolioName, 
          href: `/org-admin/${adminContext.portfolioId}/assessments`,
          state: { adminContext }
        },
        { label: adminContext.assessmentName || headerData?.portfolios?.name || "Assessment" },
      ]
    : undefined;

  const getRoleLabel = () => {
    if (isPortfolioManager) return "Portfolio Manager View";
    if (isReviewer) return "Reviewer View";
    if (isRespondent) return "Respondent View";
    return "Assessment View";
  };

  const getPrimaryCtaLabel = () => {
    if (isRespondent) return "Continue Assessment";
    if (isReviewer) return "Review Assessment";
    if (isPortfolioManager) return "View Assessment";
    return "Open Assessment";
  };

  // (getBackLabel moved below getBackRoute)

  // Check if admin came from the Admin Console (via navigation state)
  const cameFromAdminConsole = location.state?.fromAdminConsole === true;

  const handleBackNavigation = () => {
    // Admin coming from Admin Console should go back there with Assessments tab selected
    if (isAdmin && cameFromAdminConsole) {
      navigate("/admin", { state: { defaultTab: "assessments" } });
      return;
    }
    navigate(getBackRoute());
  };

  const getBackRoute = () => {
    // Check for explicit returnTo param first
    const returnTo = searchParams.get("returnTo");
    if (returnTo) {
      return returnTo;
    }
    
    // Read-only roles: back to their portfolio/assessments list
    if (isReadOnlyRole) {
      // If we have the org_id, go to the assessments list
      if (headerData?.portfolios?.id) {
        return `/org-admin/${headerData.portfolios.id}/assessments`;
      }
      // Fallback by role
      if (isClientAdmin) return "/client/sub-orgs";
      if (isAdmin) return "/admin";
      return "/pm";
    }
    if (isReviewer) return "/reviewer";
    return "/dashboard";
  };
  
  const getBackLabel = () => {
    if (isAdmin && cameFromAdminConsole) return "Back to Admin Console";
    if (isAdmin) return "Back to Admin Console";
    if (isClientAdmin) return "Back to Portfolio";
    if (isPortfolioManager) return "Back to Assessments";
    if (isReviewer) return "Back to Reviewer Dashboard";
    if (isRespondent) return "Back to My Assessments";
    return "Back";
  };

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case "submitted": return "default";
      case "in review": return "secondary";
      case "approved": return "default";
      default: return "outline";
    }
  };

  const getQuestionnaireName = () => {
    const meta = (headerData?.questionnaires?.meta ?? {}) as Record<string, unknown>;
    const title = (meta.title ?? meta.name ?? meta.label) as string | undefined;

    const cleanTitle = typeof title === "string" && title.trim().length > 0 ? title.trim() : null;

    // Only admins should ever see internal IDs
    if (isAdmin) {
      return cleanTitle ? `${cleanTitle} (${headerData?.questionnaire_id})` : (headerData?.questionnaire_id ?? "Questionnaire");
    }

    return cleanTitle ?? "Questionnaire";
  };

  const getFormattedDate = () => {
    const dateStr = headerData?.submitted_at ?? headerData?.created_at;
    if (!dateStr) return "—";
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return "—";
    }
  };

  const getDateLabel = () => {
    return headerData?.submitted_at ? "Submitted" : "Created";
  };

  // Derive scores from maturity summary RPC
  const respondentPct = maturitySummary?.respondent_completion_pct ?? 0;
  const reviewerPct = maturitySummary?.reviewer_completion_pct ?? 0;
  
  // Total notes count across all domains
  const totalNotesCount = Object.values(domainData).reduce((acc, d) => acc + d.notesCount, 0);
  
  // Hybrid overall score: use reviewer score when available and post-submission, else respondent
  // For self-assessment-only questionnaires, always use respondent score
  const selfAssessmentOnly = isSelfAssessmentOnly(headerData?.questionnaire_id);
  const effectiveOverallScore = (() => {
    if (!maturitySummary) return null;
    if (selfAssessmentOnly) return maturitySummary.respondent_overall_score;
    const status = maturitySummary.assessment_status?.toLowerCase();
    const hasReviewer = maturitySummary.reviewer_overall_score !== null;
    if (hasReviewer && ['in review', 'reviewed', 'approved', 'closed', 'submitted'].includes(status)) {
      return maturitySummary.reviewer_overall_score;
    }
    return maturitySummary.respondent_overall_score;
  })();

  // Map RPC NIST domain scores to CSV Pro domain scores
  const csvProDomainScores = (() => {
    if (!maturitySummary) return {} as Record<string, number | null>;
    const result: Record<string, number | null> = {};
    const status = maturitySummary.assessment_status?.toLowerCase();
    const useReviewer = !selfAssessmentOnly &&
      maturitySummary.reviewer_overall_score !== null &&
      ['in review', 'reviewed', 'approved', 'closed', 'submitted'].includes(status);
    const byDomain = useReviewer ? maturitySummary.reviewer_by_domain : maturitySummary.respondent_by_domain;
    
    for (const csvDomain of CSV_PRO_DOMAINS) {
      const nistFunctions = Object.entries(FRAMEWORK_TO_CSV_DOMAIN)
        .filter(([_, csvDomains]) => csvDomains.includes(csvDomain))
        .map(([fn]) => fn);
      const codes = Object.entries(NIST_CODE_TO_FUNCTION)
        .filter(([_, fn]) => nistFunctions.includes(fn))
        .map(([code]) => code);
      const scores = codes.filter(c => byDomain?.[c] !== undefined).map(c => byDomain[c]);
      result[csvDomain] = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    }
    return result;
  })();

   // Needs Attention banner logic — suppress when assessment hasn't started
   const assessmentNotStarted = respondentPct === 0;
   const anyDomainBelowThreshold = Object.values(csvProDomainScores).some(s => s !== null && s < SCORE_THRESHOLDS.ADEQUATE);
   const needsAttentionBanner = assessmentNotStarted
     ? false
     : selfAssessmentOnly
       ? ((effectiveOverallScore !== null && effectiveOverallScore < SCORE_THRESHOLDS.ADEQUATE) || anyDomainBelowThreshold)
       : ((effectiveOverallScore !== null && effectiveOverallScore < SCORE_THRESHOLDS.ADEQUATE) ||
          anyDomainBelowThreshold ||
          (totalNotesCount > 0 && reviewerPct < 50));
  
  // Reviewer status logic
  const hasReviewerAnswers = (maturitySummary?.reviewer_completion_pct ?? 0) > 0;
  const hasReviewerNotes = totalNotesCount > 0;
  const isAssessmentCompleted = headerData?.status?.toLowerCase() === "completed" || headerData?.status?.toLowerCase() === "approved";
  
  const getReviewerStatusBadge = () => {
    if (isAssessmentCompleted) {
      return <Badge className="bg-green-500 text-white hover:bg-green-600">Completed</Badge>;
    } else if (hasReviewerAnswers || hasReviewerNotes) {
      return <Badge className="bg-amber-500 text-white hover:bg-amber-600">In Progress</Badge>;
    } else {
      return <Badge variant="secondary">Not Started</Badge>;
    }
  };
  
  // Get notes for a specific CSV Pro domain
  const getNotesForDomain = (csvDomain: string) => {
    const frameworkDomains = Object.entries(FRAMEWORK_TO_CSV_DOMAIN)
      .filter(([_, csvDomains]) => csvDomains.includes(csvDomain))
      .map(([framework]) => framework);
    
    const questionIdsInDomain = new Set(
      questionInfoList
        .filter(q => frameworkDomains.includes(q.domain))
        .map(q => q.question_id)
    );
    
    const questionKeyMap: Record<number, string> = {};
    for (const q of questionInfoList) {
      questionKeyMap[q.question_id] = q.question_key;
    }
    
    return reviewerRatingsList
      .filter(r => 
        questionIdsInDomain.has(r.question_id) &&
        typeof r.reviewer_notes === "string" && 
        r.reviewer_notes.trim() !== ""
      )
      .map(r => ({
        question_id: r.question_id,
        question_key: questionKeyMap[r.question_id] || undefined,
        comment: r.reviewer_notes!,
        score: r.reviewer_score,
        evidence_links: null
      }));
  };
  
  const openNotesDrawer = (domain: string) => {
    setSelectedDomain(domain);
    setDrawerOpen(true);
  };
  
  // Handle row click on domain table
  const handleDomainRowClick = (domain: string) => {
    const stats = domainData[domain];
    if (stats && stats.notesCount > 0 && canViewNotesDrillIn) {
      openNotesDrawer(domain);
    }
  };

  const isPM = effectiveRole === "portfolio_manager";

  return (
    <AppLayout title="Assessment Overview">

      {/* Read-only banner for Portfolio Managers and Client Admins */}
      {isReadOnlyRole && (
        <div className="bg-blue-50 dark:bg-blue-950/50 border-b border-blue-200 dark:border-blue-800 px-6 py-2">
          <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
            <Eye className="h-4 w-4" />
            <span className="font-medium">Read-only view.</span>
            <span className="text-blue-600 dark:text-blue-300">
              {isClientAdmin 
                ? "Client Admins cannot modify assessments." 
                : "Portfolio Managers cannot modify assessments."}
            </span>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6 max-w-5xl mx-auto">

        {/* QA Toolkit — only renders if caller has qa_tools_enabled AND is assigned */}
        {assessmentId && <QaToolkitPanel assessmentId={assessmentId} />}

        {/* Admin Breadcrumb */}
        {isAdmin && adminBreadcrumbSegments && (
          <AdminBreadcrumb segments={adminBreadcrumbSegments} />
        )}
        
        {/* Navigation Bar - show for non-admin or admin without breadcrumb context */}
        {(!adminBreadcrumbSegments || (isAdmin && cameFromAdminConsole)) && (
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={handleBackNavigation}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {getBackLabel()}
            </Button>
          </div>
        )}

        {/* Assessment Header */}
        <div className="flex items-center justify-between">
          <div>
            {loading ? (
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">Loading assessment...</span>
              </div>
            ) : error ? (
              <div className="text-destructive">Error: {error}</div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <BrandedLogo
                    src={getLogoUrl(resolveOrgLogo(portfolioLogo, null, clientLogo))}
                    alt={headerData?.portfolios?.name || "Organization"}
                    className="h-8 w-auto"
                    fallback={<Building2 className="h-6 w-6 text-muted-foreground" />}
                  />
                  <h1 className="text-2xl font-semibold">
                    {headerData?.portfolios?.name ?? "Unknown Portfolio"}
                  </h1>
                  <Badge variant={getStatusVariant(headerData?.status ?? "")}>
                    {headerData?.status ?? "Unknown"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1 ml-9">
                  {getRoleLabel()} • {getQuestionnaireName()} • {getDateLabel()} {getFormattedDate()}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Tabs for Overview and Users */}
        {!loading && !error && (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Participants Summary - hidden for read-only roles */}
              {!isReadOnlyRole && !isTier1 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full border">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Respondents: {participantCounts.respondents}</span>
                  </div>
                  {!selfAssessmentOnly && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full border">
                    <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Reviewers: {participantCounts.reviewers}</span>
                  </div>
                  )}
                </div>
              )}

        {/* Needs Attention Banner */}
        {!loading && !error && needsAttentionBanner && (
          <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
            <span className="text-sm font-medium text-destructive">
              Needs Attention: One or more areas require review
            </span>
          </div>
        )}

        {/* 1. HERO CARD: Overall Cyber Maturity (largest) */}
        {!loading && !error && (
          <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Overall Cyber Maturity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-6">
                {(() => {
                  const localRespondentPct = maturitySummary?.respondent_completion_pct ?? 0;
                  const isNotStarted = localRespondentPct === 0;
                  const isInProgress = localRespondentPct > 0 && localRespondentPct < 100;
                  const isComplete = localRespondentPct >= 100;

                  if (isNotStarted) {
                    return (
                      <>
                        <p className="text-4xl font-semibold text-muted-foreground">Not Started</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Cycle {maturitySummary?.current_review_cycle ?? 1}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">No responses submitted yet</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">Assessment has not been initiated.</p>
                      </>
                    );
                  }

                  if (isInProgress && effectiveOverallScore === null) {
                    return (
                      <>
                        <p className="text-4xl font-semibold text-blue-600 dark:text-blue-400">In Progress</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Cycle {maturitySummary?.current_review_cycle ?? 1}
                        </p>
                        <p className="text-lg text-muted-foreground mt-1">{localRespondentPct}% Complete</p>
                      </>
                    );
                  }

                  const rating = getResilienceRating(effectiveOverallScore);
                  return (
                    <>
                      <p className={`text-4xl font-bold ${rating.colorClass}`}>
                        {rating.label}
                      </p>
                      <Badge className={`mt-3 text-sm px-3 py-1 border ${rating.bgClass}`}>
                        Resilience Rating
                      </Badge>
                      <p className="text-lg text-muted-foreground mt-2">
                        {effectiveOverallScore} / 100
                      </p>
                      {selfAssessmentOnly ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          Based on Respondent Self-Assessment
                        </p>
                      ) : maturitySummary && maturitySummary.respondent_overall_score !== null && maturitySummary.reviewer_overall_score !== null ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          Respondent: {maturitySummary.respondent_overall_score} · Reviewer: {maturitySummary.reviewer_overall_score}
                        </p>
                      ) : maturitySummary?.respondent_overall_score !== null ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          Respondent Score: {maturitySummary!.respondent_overall_score}
                        </p>
                      ) : null}
                      <p className="text-sm text-muted-foreground mt-1">
                        Cycle {maturitySummary?.current_review_cycle ?? 1}
                      </p>
                      {isInProgress && (
                        <p className="text-xs text-muted-foreground mt-1">{localRespondentPct}% Complete</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reopen Assessment button for Closed assessments (PM / Admin only) */}
        {!loading && !error && headerData?.status === "Closed" && (isPortfolioManager || isAdmin) && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={async () => {
                const { error: reopenErr } = await (supabase.rpc as any)("reopen_assessment", {
                  p_assessment_id: assessmentId,
                });
                if (reopenErr) {
                  console.error("reopen_assessment failed:", reopenErr);
                  const { toast } = await import("sonner");
                  toast.error("Failed to reopen assessment");
                } else {
                  const { toast } = await import("sonner");
                  toast.success("Assessment reopened");
                  setRefreshKey(k => k + 1);
                }
              }}
            >
              🔓 Reopen Assessment
            </Button>
          </div>
        )}

        {/* 2. THREE SMALLER CARDS ROW: Respondent Progress | Reviewer Progress | Assessment Health */}
        {!loading && !error && (
          <div className={`grid grid-cols-1 ${selfAssessmentOnly ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
            {/* Respondent Progress */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  Respondent Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-primary">{respondentPct}%</span>
                </div>
                <Progress value={respondentPct} className="h-2 mb-2" />
                <p className="text-xs text-muted-foreground">
                  Respondent completion
                </p>
              </CardContent>
            </Card>

            {/* Reviewer Progress — hidden for self-assessment */}
            {!selfAssessmentOnly && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <ClipboardCheck className="h-4 w-4" />
                  Reviewer Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-primary">{reviewerPct}%</span>
                </div>
                <Progress value={reviewerPct} className="h-2 mb-2" />
                <p className="text-xs text-muted-foreground">
                  Reviewer completion · Cycle {maturitySummary?.current_review_cycle ?? 1}
                </p>
              </CardContent>
            </Card>
            )}

            {/* Assessment Health — for self-assessment, show simplified version */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Activity className="h-4 w-4" />
                  Assessment Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {selfAssessmentOnly ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Type</span>
                    <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">
                      Self-Assessment Only
                    </Badge>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Reviewer Status</span>
                      {getReviewerStatusBadge()}
                    </div>
                    {!isRespondent && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Reviewer Notes</span>
                        {totalNotesCount > 0 ? (
                          <button
                            onClick={() => openNotesDrawer(CSV_PRO_DOMAINS[0])}
                            className="text-xs text-primary font-medium hover:underline"
                          >
                            {totalNotesCount} note{totalNotesCount !== 1 ? "s" : ""}
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">No notes</span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* 3. DOMAIN TABLE: Domain-Level Breakdown */}
        {!loading && !error && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <LayoutGrid className="h-5 w-5 text-muted-foreground" />
                Domain-Level Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {respondentPct === 0 ? (
                <div className="flex items-center justify-center py-10 border border-dashed rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Domain breakdown will appear once responses are submitted.
                  </p>
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Domain</th>
                      <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Score</th>
                      <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Completion</th>
                      {!selfAssessmentOnly && <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Reviewed</th>}
                      <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                      {!selfAssessmentOnly && <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {CSV_PRO_DOMAINS.map((domain) => {
                      const stats = domainData[domain] ?? { total: 0, respondentAnswered: 0, reviewerReviewed: 0, notesCount: 0, score: null };
                      const domainScore = csvProDomainScores[domain] ?? null;
                      const isClickable = canViewNotesDrillIn && stats.notesCount > 0;

                      // Domain status based on progress, not score
                      const domainRespondentPct = stats.total > 0
                        ? Math.round((stats.respondentAnswered / stats.total) * 100)
                        : respondentPct; // fallback to overall
                      const domainReviewerPct = stats.total > 0
                        ? Math.round((stats.reviewerReviewed / stats.total) * 100)
                        : reviewerPct;

                      let domainStatus: { label: string; variant: "default" | "secondary" | "destructive" | "outline" };
                      if (domainRespondentPct === 0) {
                        domainStatus = { label: "Not Started", variant: "outline" };
                      } else if (domainRespondentPct < 100) {
                        domainStatus = { label: "In Progress", variant: "secondary" };
                      } else if (!selfAssessmentOnly && domainReviewerPct < 100) {
                        domainStatus = { label: "Awaiting Review", variant: "secondary" };
                      } else if (domainScore !== null) {
                        const rating = getResilienceRating(domainScore);
                        domainStatus = { label: rating.label, variant: domainScore >= SCORE_THRESHOLDS.STRONG ? "default" : domainScore >= SCORE_THRESHOLDS.ADEQUATE ? "secondary" : "destructive" };
                      } else {
                        domainStatus = { label: "Complete", variant: "default" };
                      }
                      
                      return (
                        <tr 
                          key={domain} 
                          className={`border-b last:border-b-0 transition-colors ${
                            isClickable ? "hover:bg-muted/50 cursor-pointer" : "hover:bg-muted/30"
                          }`}
                          onClick={() => handleDomainRowClick(domain)}
                        >
                          <td className="py-3 px-4 text-sm font-medium">{domain}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm font-semibold">
                              {domainScore !== null ? <>{domainScore}<span className="text-muted-foreground font-normal">/100</span></> : "—"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary transition-all"
                                  style={{ width: `${respondentPct}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-8">{respondentPct}%</span>
                            </div>
                          </td>
                          {!selfAssessmentOnly && (
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-secondary transition-all"
                                  style={{ width: `${reviewerPct}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-8">{reviewerPct}%</span>
                            </div>
                          </td>
                          )}
                          <td className="py-3 px-4 text-center">
                            <Badge variant={domainStatus.variant} className="text-xs">
                              {domainStatus.label}
                            </Badge>
                          </td>
                          {!selfAssessmentOnly && (
                          <td className="py-3 px-4 text-center">
                            {isClickable ? (
                              <span className="text-sm text-primary font-medium">
                                {stats.notesCount}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {stats.notesCount > 0 ? stats.notesCount : "—"}
                              </span>
                            )}
                          </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 4. COLLAPSIBLE: Reviewer Notes (collapsed by default) */}
        {!isRespondent && !selfAssessmentOnly && !isTier1 && (
          <Collapsible open={notesExpanded} onOpenChange={setNotesExpanded}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      Reviewer Notes
                      {totalNotesCount > 0 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {totalNotesCount}
                        </Badge>
                      )}
                    </div>
                    {notesExpanded ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {totalNotesCount === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No reviewer notes yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {CSV_PRO_DOMAINS.map((domain) => {
                        const stats = domainData[domain] ?? { total: 0, respondentAnswered: 0, reviewerReviewed: 0, notesCount: 0, score: null };
                        const isClickable = canViewNotesDrillIn && stats.notesCount > 0;
                        
                        if (stats.notesCount === 0) return null;
                        
                        return (
                          <div 
                            key={domain} 
                            className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                              isClickable 
                                ? "hover:bg-muted/50 cursor-pointer bg-muted/30" 
                                : "bg-muted/20"
                            }`}
                            onClick={isClickable ? () => openNotesDrawer(domain) : undefined}
                            role={isClickable ? "button" : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            onKeyDown={isClickable ? (e) => e.key === "Enter" && openNotesDrawer(domain) : undefined}
                          >
                            <span className={`text-sm ${isClickable ? "text-primary font-medium" : ""}`}>
                              {domain}
                            </span>
                            <span className={`text-sm ${isClickable ? "text-primary font-medium" : "text-muted-foreground"}`}>
                              {stats.notesCount} note{stats.notesCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

              {/* Primary Action Button */}
              <div className="flex gap-3 pt-4 border-t">
                {effectiveRole ? (
                  <Button 
                    size="lg" 
                    onClick={() => {
                      // Read-only roles (PM, client_admin) go to read-only questionnaire view
                      if (isReadOnlyRole) {
                        navigate(`/assessments/${assessmentId}/questionnaire`);
                      } else {
                        navigate(`/wizard/${assessmentId}`);
                      }
                    }}
                  >
                    {isReadOnlyRole ? (
                      <>
                        <Eye className="h-5 w-5 mr-2" />
                        View Questionnaire
                      </>
                    ) : (
                      <>
                        <FileText className="h-5 w-5 mr-2" />
                        {getPrimaryCtaLabel()}
                      </>
                    )}
                  </Button>
                ) : (
                  <Button size="lg" variant="outline" disabled>
                    <FileText className="h-5 w-5 mr-2" />
                    Loading...
                  </Button>
                )}
              </div>
            </TabsContent>

            {/* Users Tab - PM can manage users, others are read-only */}
            <TabsContent value="users">
              {assessmentId && headerData?.portfolios?.id && (
                <AssessmentUsersTab
                  assessmentId={assessmentId}
                  portfolioId={headerData.portfolios.id}
                  portfolioName={headerData.portfolios.name ?? "Unknown"}
                  readOnly={!isPortfolioManager}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
      
      {/* Reviewer Notes Drawer */}
      <ReviewerNotesDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        domainName={selectedDomain}
        notes={getNotesForDomain(selectedDomain)}
      />
    </AppLayout>
  );
}
